import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';

import { PHONE_QUERY, useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cn } from '@/shared/utils/cn';

import { Icon, type IconName } from './Icon';
import { Sheet } from './Sheet';

/** One row of a menu: a destination (`to`) or an action (`onSelect`). */
export interface MenuItem {
  /** Stable React key; the label when absent. */
  id?: string;
  label: string;
  /** A destination: the row is a link. */
  to?: string;
  /**
   * An action: the row is a button. It runs once the menu has closed and
   * focus is back on the trigger, so a dialog it opens returns focus there.
   */
  onSelect?: () => void;
  icon?: IconName;
  /** The label's language, where it differs from the screen's. */
  lang?: string;
  /** R-050: `danger` rows go last, after a divider, and are the only red rows. */
  tone?: 'default' | 'danger';
  /** Secondary text at the row's end ("English" beside "Display language"). */
  trailing?: ReactNode;
}

export type MenuEntry = MenuItem | { divider: true };

export interface MenuProps {
  /** The trigger's accessible name, and the menu's. */
  label: string;
  /**
   * The trigger's accessible description: what its own text says beyond its
   * name, which `label` replaces for assistive technology (OBS-007: the
   * account trigger's roles). The name stays `label`.
   */
  description?: string;
  /** What the trigger shows — an avatar, a name. */
  trigger: ReactNode;
  /**
   * R-050 identity header: who or what the menu is about. `details` are
   * facts under the title and meta (OBS-007: the person's roles and where they
   * work) — read-only, never a row: part of the menu's description, and on a
   * phone at the top of the sheet, before its rows.
   */
  header?: { title: string; meta?: ReactNode; details?: ReactNode };
  items: MenuEntry[];
  /**
   * Which edge of the trigger the menu lines up with (logical, so it follows
   * `dir`). R-050 anchors at the inline start; a trigger at the END of a bar
   * (the account control) lines up at its end instead, or the menu would hang
   * off the screen.
   */
  align?: 'start' | 'end';
  triggerClassName?: string;
  /** While an item's work is in flight (signing out). */
  busy?: boolean;
}

/** The phone shows a menu of five or more items as a bottom sheet (addendum 04 §Overlays). */
const SHEET_FROM = 5;

const isDivider = (entry: MenuEntry): entry is { divider: true } => 'divider' in entry;

/**
 * R-050 Menu — the menu-button pattern (WAI-ARIA APG).
 *
 * - The trigger says what it opens (`aria-haspopup`, `aria-expanded`) and is
 *   named; Enter, Space or ArrowDown opens on the first item, ArrowUp on the
 *   last.
 * - In the menu: ArrowDown/ArrowUp move and wrap, Home/End jump; Escape and
 *   Tab close and return focus to the trigger; a press outside closes.
 * - Rows are 44px; the menu lines up with the trigger's inline edge; its rows
 *   are at most 320px tall and scroll beyond that. The identity header stays
 *   above them and never scrolls away (OBS-007, PROPOSED: R-050 counts the
 *   header inside the 320px, which an account header with role context would
 *   overflow, putting Sign out below the fold). On a screen too short for
 *   both, the rows give way and scroll; the header does not.
 * - Choosing a row closes the menu FIRST and puts focus back on the trigger,
 *   then runs the row's action: a dialog the action opens returns focus to a
 *   connected element when it closes (the RowActions lesson).
 *
 * On a phone, a menu of five or more items is a bottom sheet holding a plain
 * list of links and buttons (R-050 addendum 04 §Overlays): the platform dialog
 * traps focus, Escape and the scrim close it, and focus returns to the trigger.
 *
 * The menu decides nothing. It renders the rows it is given; whether a row
 * exists is the caller's permission decision, and the server re-checks every
 * destination and action.
 */
export function Menu({
  label,
  description,
  trigger,
  header,
  items,
  align = 'start',
  triggerClassName,
  busy = false,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const [focusOn, setFocusOn] = useState<'first' | 'last'>('first');
  const phone = useMediaQuery(PHONE_QUERY);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const pending = useRef<(() => void) | null>(null);
  const menuId = useId();
  const headerId = useId();
  const descriptionId = useId();

  const rows = items.filter((entry): entry is MenuItem => !isDivider(entry));
  const asSheet = phone && rows.length >= SHEET_FROM;

  // Opening: the first (or last) row takes focus, so the keyboard path continues.
  useEffect(() => {
    if (!open) return;

    const list = rowsIn(listRef.current);
    (focusOn === 'last' ? list.at(-1) : list.at(0))?.focus();
  }, [open, focusOn]);

  // Closing after a choice: focus back on the trigger, THEN the row's action.
  useEffect(() => {
    if (open) return;

    const run = pending.current;
    pending.current = null;

    if (run === null) return;

    triggerRef.current?.focus();
    run();
  }, [open]);

  // A press anywhere else closes the dropdown (the sheet has its own scrim).
  useEffect(() => {
    if (!open || asSheet) return;

    const onPointer = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);

    return () => document.removeEventListener('mousedown', onPointer);
  }, [open, asSheet]);

  const show = (from: 'first' | 'last') => {
    setFocusOn(from);
    setOpen(true);
  };

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const choose = (item: MenuItem) => {
    pending.current = item.onSelect ?? null;
    // A destination still returns focus to the trigger; the page changes under it.
    if (pending.current === null) triggerRef.current?.focus();
    setOpen(false);
  };

  const onTriggerKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      show(event.key === 'ArrowUp' ? 'last' : 'first');
    }
  };

  const onMenuKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = rowsIn(listRef.current);
    const at = list.indexOf(document.activeElement as HTMLElement);
    const move = (index: number) => {
      event.preventDefault();
      list[(index + list.length) % list.length]?.focus();
    };

    switch (event.key) {
      case 'ArrowDown':
        move(at + 1);
        break;
      case 'ArrowUp':
        move(at < 0 ? list.length - 1 : at - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(list.length - 1);
        break;
      case 'Escape':
      case 'Tab':
        // In the sheet, the dialog owns both: Escape closes it, Tab stays inside.
        if (asSheet) break;
        event.preventDefault();
        close(true);
        break;
      case ' ':
        // A link row answers Space as a button row does (APG menuitem).
        if (at >= 0 && list[at].tagName === 'A') {
          event.preventDefault();
          list[at].click();
        }
        break;
      default:
        break;
    }
  };

  const triggerButton = (
    <button
      ref={triggerRef}
      type="button"
      aria-label={label}
      aria-describedby={description ? descriptionId : undefined}
      aria-haspopup={asSheet ? 'dialog' : 'menu'}
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      aria-busy={busy || undefined}
      onClick={() => (open ? close(false) : show('first'))}
      onKeyDown={onTriggerKey}
      className={cn(
        'inline-flex min-h-11 min-w-11 items-center justify-center gap-2.5 rounded-lg px-1 text-start',
        'hover:bg-hover focus-visible:outline-2 focus-visible:outline-focus',
        triggerClassName,
      )}
    >
      {trigger}
      {/* Never displayed: only read, as the trigger's description. */}
      {description ? (
        <span id={descriptionId} hidden>
          {description}
        </span>
      ) : null}
    </button>
  );

  if (asSheet) {
    return (
      <>
        {triggerButton}
        <Sheet
          id={menuId}
          open={open}
          side="bottom"
          title={header?.title ?? label}
          subtitle={header?.meta}
          onClose={() => setOpen(false)}
        >
          {open ? (
            <>
              {header?.details ? (
                <div className="border-b border-line-subtle px-4 py-3 text-start">
                  {header.details}
                </div>
              ) : null}
              <div ref={listRef} className="px-2 py-2" onKeyDown={onMenuKey}>
                {groupsOf(items).map((group, index) => (
                  <ul
                    key={group.map((item) => item.id ?? item.label).join('|')}
                    className={cn(index > 0 && 'mt-1 border-t border-line-subtle pt-1')}
                  >
                    {group.map((item) => (
                      <li key={item.id ?? item.label}>
                        <Row item={item} role={undefined} onChoose={() => choose(item)} />
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </>
          ) : null}
        </Sheet>
      </>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="relative"
      onBlur={(event) => {
        // Focus left the menu for somewhere else on the page: it closes.
        const next = event.relatedTarget as Node | null;
        if (open && next !== null && !wrapRef.current?.contains(next)) setOpen(false);
      }}
    >
      {triggerButton}

      {open ? (
        <div
          className={cn(
            'absolute top-full z-40 mt-1.5 flex max-h-[calc(100dvh-var(--layout-topbar-h)-0.75rem)] w-[250px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden',
            'rounded-xl border border-line-subtle bg-card shadow-(--shadow-md)',
            align === 'end' ? 'end-0' : 'start-0',
          )}
        >
          {header ? (
            <div
              id={headerId}
              className="flex-none border-b border-line-subtle px-[15px] py-[13px] text-start"
            >
              <p className="type-card-title wrap-break-word text-ink">{header.title}</p>
              {header.meta ? (
                <p className="type-meta mt-0.5 wrap-break-word text-ink-muted">{header.meta}</p>
              ) : null}
              {header.details ? <div className="mt-0.5">{header.details}</div> : null}
            </div>
          ) : null}
          {/* The rows scroll, and only past 320px: never the header with them. */}
          <div
            ref={listRef}
            id={menuId}
            role="menu"
            aria-label={label}
            aria-describedby={header ? headerId : undefined}
            className="max-h-80 min-h-0 overflow-y-auto p-[5px]"
            onKeyDown={onMenuKey}
          >
            {items.map((entry, index) =>
              isDivider(entry) ? (
                <div
                  key={`divider-${index}`}
                  role="separator"
                  className="mx-0 my-[5px] h-px bg-line-subtle"
                />
              ) : (
                <Row
                  key={entry.id ?? entry.label}
                  item={entry}
                  role="menuitem"
                  onChoose={() => choose(entry)}
                />
              ),
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The focusable rows, in order. */
function rowsIn(list: HTMLElement | null): HTMLElement[] {
  return list ? [...list.querySelectorAll<HTMLElement>('[data-menu-row]')] : [];
}

/** The rows between dividers, for the phone sheet's plain lists. */
function groupsOf(entries: MenuEntry[]): MenuItem[][] {
  const groups: MenuItem[][] = [[]];

  for (const entry of entries) {
    if (isDivider(entry)) groups.push([]);
    else groups[groups.length - 1].push(entry);
  }

  return groups.filter((group) => group.length > 0);
}

function Row({
  item,
  role,
  onChoose,
}: {
  item: MenuItem;
  role: 'menuitem' | undefined;
  onChoose: () => void;
}) {
  const className = cn(
    'type-ui flex min-h-11 w-full items-center gap-[9px] rounded-(--radius-sm) px-3 py-[9px] text-start',
    'hover:bg-hover focus:bg-hover focus-ring-inset focus-visible:outline-2 focus-visible:outline-focus',
    item.tone === 'danger' ? 'text-critical-ink' : 'text-ink',
  );
  const content = (
    <>
      {item.icon ? <Icon name={item.icon} size={15} className="shrink-0" /> : null}
      <span lang={item.lang} className="min-w-0 flex-1 wrap-break-word">
        {item.label}
      </span>
      {item.trailing != null ? (
        <span className="type-meta shrink-0 font-semibold text-ink-muted">{item.trailing}</span>
      ) : null}
    </>
  );

  if (item.to !== undefined) {
    return (
      <Link
        to={item.to}
        role={role}
        tabIndex={role === 'menuitem' ? -1 : undefined}
        data-menu-row=""
        className={className}
        onClick={onChoose}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      role={role}
      tabIndex={role === 'menuitem' ? -1 : undefined}
      data-menu-row=""
      className={className}
      onClick={onChoose}
    >
      {content}
    </button>
  );
}
